import { getReferralRewardsQuery, type ReferralRewardFilters } from '@constants/graphQLQueries';
import { humanizeEnum } from '@helpers/labels';

import { referrerLabel } from './referrer';
import { rewardKind, rewardTitle, type RewardRow } from './review';

/**
 * The payout sheet: every reward matching the Payouts filters, across every page, as CSV.
 *
 * CSV rather than the styled workbook the applications list exports, because this one is read by
 * a person paying by UPI and by a bank upload, both of which want plain columns. It carries the
 * payee's UPI ID and phone, which is personal data: the page that offers it is Super Admin only,
 * and so is the query behind it (BFF and backend).
 */

/** Large enough to keep the request count low, small enough that one page is a quick answer. */
export const EXPORT_PAGE_SIZE = 200;
/** A ceiling, so a backend that keeps answering full pages cannot loop the browser forever. */
const MAX_PAGES = 100;

export const PAYOUT_CSV_HEADER = [
  'Reward ID',
  'Kind',
  'Title',
  'Amount',
  'Currency',
  'Status',
  'Referrer type',
  'Referrer name',
  'Referrer email',
  'Payout UPI ID',
  'Payout phone',
  'Earned at',
  'Paid at',
  'Payment reference',
];

/*
 * One cell. Quoted when it holds a comma, quote or line break. A leading = + - @ is prefixed with a
 * quote mark: names and references are typed by referrers, and a spreadsheet would otherwise run
 * "=HYPERLINK(...)" as a formula. Numbers are written as they are, so amounts still add up.
 */
export const csvCell = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  let s = String(value);
  // A phone number such as "+91 98200 10001" is digits, so it cannot be a formula; leave it readable.
  if (/^[=+\-@\t\r]/.test(s) && !/^[+-]?[\d\s().-]+$/.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const payoutCsvRow = (r: RewardRow): unknown[] => [
  r.id,
  humanizeEnum(rewardKind(r)),
  rewardTitle(r),
  r.amount,
  r.currency,
  r.status,
  referrerLabel(r.referrerType),
  r.referrerName ?? '',
  r.referrerEmail ?? '',
  r.payoutUpiId ?? '',
  r.payoutPhone ?? '',
  r.earnedAt ?? '',
  r.paidAt ?? '',
  r.paymentRef ?? '',
];

/** The whole file. CRLF line ends, which is what RFC 4180 and Excel both expect. */
export const payoutCsv = (rows: RewardRow[]): string =>
  [PAYOUT_CSV_HEADER, ...rows.map(payoutCsvRow)].map((line) => line.map(csvCell).join(',')).join('\r\n');

type Fetcher = (doc: string) => Promise<any>;

/** Every row matching the filters, paged through at EXPORT_PAGE_SIZE. */
export const fetchAllPayouts = async (
  fetcher: Fetcher,
  status: string,
  filters: ReferralRewardFilters,
): Promise<RewardRow[]> => {
  const out: RewardRow[] = [];
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const r = await fetcher(getReferralRewardsQuery(status, page, EXPORT_PAGE_SIZE, filters));
    const p = r?.data?.getReferralRewards;
    const items: RewardRow[] = (p?.items ?? []).filter(Boolean);
    out.push(...items);
    const total = Number(p?.total ?? 0);
    if (items.length < EXPORT_PAGE_SIZE || out.length >= total) break;
  }
  return out;
};

/** Hands the file to the browser. The BOM makes Excel read the rupee sign and names as UTF-8. */
export const downloadCsv = (filename: string, text: string) => {
  const blob = new Blob([`﻿${text}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
